import type { QuoteOutcome, QuoteLostReason } from '@/features/trade/utils/quote-outcome';

// ── Import Order Status ──────────────────────────────────────────

export type ImportOrderStatus =
  | 'QUOTE'
  | 'DRAFT'
  | 'CONFIRMED'
  | 'SHIPPED'
  | 'IN_TRANSIT'
  | 'CUSTOMS'
  | 'CUSTOMS_HOLD'
  | 'DELIVERED'
  | 'CANCELLED';

export type ImportDetailTab =
  | 'general'
  | 'entities'
  | 'products'
  | 'containers'
  | 'costs'
  | 'documents'
  | 'tracking'
  | 'history';

// ── Transport Mode ───────────────────────────────────────────────

export type TransportMode = 'SEA' | 'AIR' | 'LAND' | 'RAIL' | 'MULTIMODAL';

// ── Incoterms ────────────────────────────────────────────────────

export type Incoterm =
  | 'EXW'
  | 'FCA'
  | 'FAS'
  | 'FOB'
  | 'CFR'
  | 'CIF'
  | 'CPT'
  | 'CIP'
  | 'DAP'
  | 'DPU'
  | 'DDP';

// ── Supplier ─────────────────────────────────────────────────────
// ImportSupplier ahora es un alias de FreightProvider (consolidación
// del catálogo). Los campos contactName/contactEmail/contactPhone se
// preservan derivados de FreightProvider.contactInfo cuando aplica.

export interface ImportSupplier {
  id: string;
  name: string;
  country?: string;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  website?: string | null;
  isActive: boolean;
  createdAt: string;
}

// ── Import Order ─────────────────────────────────────────────────

export interface ImportOrder {
  id: string;
  orderNumber: string;
  status: ImportOrderStatus;
  /** Tipo de operación: IMPORT (default, retrocompat) o EXPORT. */
  tradeType: TradeType;
  freightProviderId: string | null;
  freightProvider: ImportSupplier | null;
  /** @deprecated mantener para retrocompat — usar freightProviderId */
  supplierId?: string;
  /** @deprecated mantener para retrocompat — usar freightProvider */
  supplier?: ImportSupplier;

  // Dates
  orderDate: string;
  estimatedShipDate: string | null;
  actualShipDate: string | null;
  estimatedArrivalDate: string | null;
  actualArrivalDate: string | null;
  customsClearanceDate: string | null;
  deliveryDate: string | null;

  // Terms
  incoterm: Incoterm;
  paymentTerms: string | null;
  transportMode: TransportMode;
  originPort: string | null;
  destinationPort: string | null;
  shippingLine: string | null;
  containerNumber: string | null;
  blNumber: string | null;

  // Quién la creó (el cotizador). La gerente lo usa para saber de quién es cada una.
  createdBy?: { id: string; firstName: string; lastName: string } | null;

  // Totals
  // 🔴 El campo real que manda la API es `totalFobValue` (columna Prisma). Antes el
  // tipo decía `totalFob`, que nunca llegaba → las tarjetas del tablero mostraban
  // "USD 0" en TODAS las órdenes, incluso las que tenían FOB. (No confundir con el
  // `totalFob` de los agregados de analítica, que sí existe con ese nombre.)
  totalFobValue: number | null;
  totalFreight: number;
  totalInsurance: number;
  totalDuties: number;
  totalTaxes: number;
  totalInternacion: number;
  totalLandedCost: number;
  currency: string;

  // Counts
  _count: {
    items: number;
    documents: number;
    trackingEvents: number;
    /** Cuántas veces se recotizó a partir de esta. */
    requotes?: number;
  };

  // Customer
  customerId?: string | null;
  customer?: { id: string; name: string } | null;

  // Freight route & quote fields
  freightRouteId: string | null;
  freightLegBreakdown: FreightLegBreakdownItem[] | null;
  isQuote: boolean;
  quotedAt: string | null;
  quoteValidUntil: string | null;
  convertedToOrderAt: string | null;

  /**
   * Qué contestó el cliente. **No es la vigencia**: una cotización puede estar
   * vencida y aceptada a la vez —el cliente dijo que sí sobre un precio que ya
   * hay que recotizar—, y las dos cosas hay que verlas juntas.
   */
  quoteOutcome?: QuoteOutcome;
  quoteSentAt?: string | null;
  quoteClosedAt?: string | null;
  quoteLostReason?: QuoteLostReason | null;
  quoteLostNotes?: string | null;
  /** De qué cotización salió esta, si nació de recotizar o duplicar. */
  quotedFromId?: string | null;
  /** La cotización de origen, con su número: un uuid no le dice nada a nadie. */
  quotedFrom?: { id: string; orderNumber: string } | null;
  /**
   * El equipo por el que se cotizó (20ST, 40HQ…).
   *
   * Se cotiza POR equipo, así que sin esto el PDF no puede decir qué se le está
   * cotizando al cliente — y el contenedor tendría que volver a preguntarlo.
   */
  quotedContainerTypeId: string | null;
  /** Peso de la carga informado al cotizar. Define qué transportistas aplican. */
  quotedWeightKg: number | null;

  // Portal cliente
  publicTrackingToken: string | null;
  publicTrackingExpiresAt: string | null;

  notes: string | null;

  // ── Paridad Magaya (tab General) ───────────────────────────
  bookingNumber: string | null;
  customerReference: string | null;
  issuancePlace: string | null;
  issuanceDate: string | null;
  deliveryType: string | null;
  generalOrderDate: string | null;
  documentationCutoffDate: string | null;
  declaredValue: number | null;
  cargoDescription: string | null;

  createdAt: string;
  updatedAt: string;
  legs?: ImportOrderLeg[];
  parties?: ImportOrderParty[];
}

// ── Import Order Party (Magaya tab "Entidades") ──────────────────

export type ImportPartyType =
  | 'SHIPPER' // Expedidor
  | 'CONSIGNEE' // Último Consignatario
  | 'NOTIFY_PARTY' // Entidad a Notificar
  | 'CUSTOMS_AGENT' // Consignatario / Agente de Aduana
  | 'FORWARDER' // Agente de Carga
  | 'DESTINATION_AGENT'; // Agente de Destino

export const PARTY_TYPE_LABEL: Record<ImportPartyType, string> = {
  SHIPPER: 'Expedidor',
  CONSIGNEE: 'Último Consignatario',
  NOTIFY_PARTY: 'Entidad a Notificar',
  CUSTOMS_AGENT: 'Agente de Aduana',
  FORWARDER: 'Agente de Carga',
  DESTINATION_AGENT: 'Agente de Destino',
};

// Orden visual de las partes en la UI (sigue el orden de Magaya).
export const PARTY_TYPE_ORDER: ImportPartyType[] = [
  'SHIPPER',
  'CONSIGNEE',
  'NOTIFY_PARTY',
  'CUSTOMS_AGENT',
  'FORWARDER',
  'DESTINATION_AGENT',
];

export interface ImportOrderParty {
  id: string;
  orderId: string;
  type: ImportPartyType;
  name: string;
  address: string | null;
  taxId: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateImportPartyInput {
  type: ImportPartyType;
  name: string;
  address?: string | null;
  taxId?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

export type UpdateImportPartyInput = Partial<CreateImportPartyInput>;

// ── Import Order Item ────────────────────────────────────────────

export interface ImportOrderItem {
  id: string;
  orderId: string;
  materialId: string | null;
  material: { id: string; name: string; code: string } | null;
  description: string;
  hsCode: string | null;
  quantity: number;
  unitOfMeasure: string;
  unitFobPrice: number;
  totalFobPrice: number;
  weightKg: number | null;
  volumeCbm: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  colorHex: string | null;
  dutyRate: number | null;
  dutyAmount: number | null;
  unitLandedCost: number | null;
  allocatedFreight: number | null;
  allocatedInsurance: number | null;
  allocatedTax: number | null;
  allocatedInternacion: number | null;
  allocatedOtherCosts: number | null;
  totalItemLandedCost: number | null;
  createdAt: string;
}

// ── Cost Breakdown ───────────────────────────────────────────────

export interface CostBreakdown {
  fob: number;
  freight: number;
  insurance: number;
  duties: number;
  taxes: number;
  internacion: number;
  otherCosts: CostLineItem[];
  totalLandedCost: number;
}

export interface CostLineItem {
  id: string;
  concept: string;
  amount: number;
  notes: string | null;
}

// ── Document ─────────────────────────────────────────────────────

export type ImportDocumentType =
  | 'BILL_OF_LADING'
  | 'COMMERCIAL_INVOICE'
  | 'PACKING_LIST'
  | 'CERTIFICATE_OF_ORIGIN'
  | 'PHYTOSANITARY'
  | 'CUSTOMS_DECLARATION'
  | 'INSURANCE_POLICY'
  | 'OTHER';

export interface ImportDocument {
  id: string;
  orderId: string;
  type: ImportDocumentType;
  name: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

// ── Tracking Event ───────────────────────────────────────────────

export interface TrackingEvent {
  id: string;
  orderId: string;
  status: ImportOrderStatus;
  title: string;
  description: string | null;
  location: string | null;
  eventDate: string;
  createdBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  legId?: string | null;
  leg?: {
    id: string;
    legOrder: number;
    origin: string;
    destination: string;
    transportMode: TransportMode;
  } | null;
}

// ── Audit Log ────────────────────────────────────────────────────

export interface ImportAuditLog {
  id: string;
  orderId: string;
  action: string;
  field: string | null;
  previousValue: string | null;
  newValue: string | null;
  user: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

// ── Dashboard KPIs ───────────────────────────────────────────────

export interface ImportDashboardStats {
  totalActive: number;
  inTransit: number;
  inCustoms: number;
  deliveredThisMonth: number;
  totalFobThisMonth: number;
  totalLandedCostThisMonth: number;
  avgDaysInTransit: number;
  avgDaysInCustoms: number;
  /**
   * Conversión de cotizaciones: de las que salieron, cuántas se volvieron
   * embarque.
   *
   * Se mide con `isQuote` (el origen, permanente) y no con `status = QUOTE` (el
   * estado actual): con el segundo la tasa saldría siempre 0, porque al
   * convertirse dejan de tener ese estado.
   */
  quoteConversion?: {
    totalQuoted: number;
    converted: number;
    pending: number;
    /** `null` si no se cotizó nada: 0 % sobre cero sería una afirmación falsa. */
    ratePercent: number | null;
  };
}

export interface ImportsByStatusChart {
  status: ImportOrderStatus;
  count: number;
}

export interface MonthlyImportTrend {
  month: string;
  orderCount: number;
  totalFob: number;
  totalLandedCost: number;
}

export interface TopSupplier {
  supplierId: string;
  supplierName: string;
  orderCount: number;
  totalFob: number;
}

export interface CostVarianceAlert {
  id: string;
  orderId: string;
  orderNumber: string;
  concept: string;
  expectedAmount: number;
  actualAmount: number;
  variancePercent: number;
  createdAt: string;
}

// ── Landed Cost Calculator ───────────────────────────────────────

export interface LandedCostInput {
  fobPrice: number;
  weightKg: number;
  volumeCbm: number;
  hsCode: string;
  originCountry: string;
  destinationCountry: string;
  incoterm: Incoterm;
  insuranceRate?: number;
  transportMode?: TransportMode;
}

/**
 * Qué tan grave es un aviso de la liquidación.
 *
 * `PROHIBICION` no es "una advertencia más fuerte": es de otra clase. Una
 * advertencia dice que el número puede estar mal; una prohibición dice que la
 * operación no existe.
 */
export type NivelAviso = 'PROHIBICION' | 'ADVERTENCIA';

/**
 * Identificador estable de cada aviso. La pantalla decide cómo mostrarlo por el
 * CÓDIGO, nunca leyendo el texto: así reescribir un mensaje no rompe la
 * presentación.
 */
export type CodigoAviso =
  | 'IMPORTACION_PROHIBIDA'
  | 'GA_ESTIMADO'
  | 'GA_MANUAL'
  | 'ICE_NO_INCLUIDO'
  | 'IEHD_NO_INCLUIDO'
  | 'ICE_SIN_DATO'
  | 'DOC_DESPACHO_REQUERIDO'
  | 'FLETE_AEREO_SIN_TOPE'
  | 'FLETE_REFERENCIA'
  | 'INTERNACION_SUPUESTA'
  | 'PRECIO_DE_PARTIDA_PROHIBIDA';

export interface Aviso {
  nivel: NivelAviso;
  codigo: CodigoAviso;
  mensaje: string;
  /** La norma que lo respalda, cuando la hay: "D.S. 28761". */
  norma?: string;
  codigoPartida?: string;
}

/** De dónde salió la alícuota de arancel que se usó. */
export type OrigenAlicuota = 'ARANCEL_OFICIAL' | 'MANUAL' | 'ESTIMADO';

/**
 * 🔴 Todo lo que está debajo de `costBreakdownPercent` lo venía mandando el
 * backend desde siempre y este tipo NO lo declaraba, así que TypeScript lo
 * descartaba en silencio: la pantalla mostraba el total con la misma cara de
 * seguridad tanto si el arancel salió del arancel oficial como si era un 10 %
 * inventado, y ninguno de los avisos del backend llegaba nunca al usuario.
 */
export interface LandedCostResult {
  fob: number;
  freight: number;
  insurance: number;
  cif: number;
  dutyRate: number;
  dutyAmount: number;
  /** La tasa EFECTIVA que se aplicó: 14,94 %, no la nominal. */
  taxRate: number;
  /** La tasa nominal del tenant (13 %). Es la que la gente reconoce. */
  taxRateNominal?: number;
  taxAmount: number;
  internacion: number;
  totalLandedCost: number;
  landedCostPerUnit: number;
  costBreakdownPercent: {
    fob: number;
    freight: number;
    insurance: number;
    duties: number;
    taxes: number;
    internacion: number;
  };
  /** La clasificación arancelaria que se usó, si se pudo resolver. */
  partida?: {
    code: string;
    descripcion: string;
    contexto: string | null;
    exentaIva: boolean;
    /** `null` = no tenemos el dato. NO es "se puede importar". */
    prohibida: boolean | null;
  } | null;
  origenGa?: OrigenAlicuota;
  /** La liquidación paso a paso, cada uno con su norma. */
  pasos?: string[];
  avisos?: Aviso[];
}

// ── API Responses ────────────────────────────────────────────────

export interface ImportOrdersResponse {
  orders: ImportOrder[];
  total: number;
  page: number;
  totalPages: number;
}

export interface ImportOrdersListParams {
  search?: string;
  status?: ImportOrderStatus | '';
  freightProviderId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /**
   * Etapa del embarque. Expresa la intención en vez de filtrar por el nombre
   * de un campo:
   *
   *   `quote`        cotizaciones esperando respuesta  (status = QUOTE)
   *   `operational`  embarques en curso                (status ≠ QUOTE)
   *
   * Usar esto y **no** `isQuote`: ese marca el ORIGEN y queda en `true` para
   * siempre, así que una cotización ya convertida aparecía en la lista de
   * cotizaciones y desaparecía de la de embarques.
   */
  /**
   * `quote-history` trae **todo lo que se cotizó alguna vez**, incluidas las que
   * ya se volvieron embarque. Es la única forma de ver cuáles se ganaron: el
   * tablero decía "7 de 8 cerradas se ganaron" y esas 7 no estaban en ninguna
   * lista, porque habían salido de `status = QUOTE`.
   */
  stage?: 'quote' | 'operational' | 'quote-history';
  /** @deprecated Es el origen, no el estado actual. Preferir `stage`. */
  isQuote?: boolean;
}

// ── Create / Update Inputs ───────────────────────────────────────

export type TradeType = 'IMPORT' | 'EXPORT';

// Campos paridad Magaya — comparten ambos create y update.
export interface MagayaGeneralInput {
  bookingNumber?: string | null;
  customerReference?: string | null;
  issuancePlace?: string | null;
  issuanceDate?: string | null;
  deliveryType?: string | null;
  generalOrderDate?: string | null;
  documentationCutoffDate?: string | null;
  declaredValue?: number | null;
  cargoDescription?: string | null;
}

export interface CreateImportOrderInput extends MagayaGeneralInput {
  orderNumber?: string;
  freightProviderId?: string;
  customerId: string;
  orderDate: string;
  estimatedShipDate?: string;
  estimatedArrivalDate?: string;
  incoterm: Incoterm;
  paymentTerms?: string;
  transportMode?: TransportMode;
  tradeType?: TradeType;
  originPort?: string;
  destinationPort?: string;
  currency?: string;
  notes?: string;
  isQuote?: boolean;
  freightRouteId?: string | null;
  quoteValidUntil?: string;
}

export interface FreightLegBreakdownItem {
  legId: string;
  legOrder: number;
  origin: string;
  destination: string;
  transportMode: string;
  legType: string;
  ratePerCbm: number;
  ratePerKg: number;
  amount: number;
  included: boolean;
  estimatedDays: number | null;
}

export interface UpdateImportOrderInput extends Omit<
  Partial<CreateImportOrderInput>,
  'customerId'
> {
  customerId?: string | null;
  status?: ImportOrderStatus;
  actualShipDate?: string;
  actualArrivalDate?: string;
  customsClearanceDate?: string;
  deliveryDate?: string;
  shippingLine?: string;
  containerNumber?: string;
  blNumber?: string;
  freightRouteId?: string | null;
}

export interface CreateImportItemInput {
  materialId?: string;
  description: string;
  hsCode?: string;
  quantity: number;
  unitOfMeasure: string;
  unitFobPrice: number;
  weightKg?: number;
  volumeCbm?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  colorHex?: string;
  dutyRate?: number;
}

export interface CreateTrackingEventInput {
  status: ImportOrderStatus;
  title: string;
  description?: string;
  location?: string;
  eventDate: string;
  legId?: string | null;
}

export interface CreateImportSupplierInput {
  name: string;
  country: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
}

// ── Container Types ─────────────────────────────────────────────

export type ContainerStatus =
  | 'EMPTY'
  | 'LOADING'
  | 'SEALED'
  | 'IN_TRANSIT'
  | 'AT_PORT'
  | 'DELIVERED';

export interface ContainerType {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  maxVolumeCbm: number;
  maxWeightKg: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Container {
  id: string;
  tenantId: string;
  orderId: string;
  containerTypeId: string;
  containerType?: ContainerType;
  containerNumber?: string;
  sealNumber?: string;
  status: ContainerStatus;
  originPort?: string;
  destinationPort?: string;
  etd?: string;
  eta?: string;
  atd?: string;
  ata?: string;
  usedVolumeCbm: number;
  usedWeightKg: number;
  fillPercent: number;
  items?: ContainerItem[];
  _count?: { items: number };
  createdAt: string;
  updatedAt: string;
}

export interface ContainerItem {
  id: string;
  containerId: string;
  materialId?: string;
  material?: { id: string; name: string; code: string };
  description: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  weightKg: number;
  quantity: number;
  color: string;
  positionX?: number;
  positionY?: number;
  positionZ?: number;
  isRotated: boolean;
  /** Dónde va cada unidad. `null` en las líneas sin acomodo fino. */
  unitPositions?: UnitPosition[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateContainerInput {
  containerTypeId: string;
  containerNumber?: string;
  sealNumber?: string;
  originPort?: string;
  destinationPort?: string;
  etd?: string;
  eta?: string;
}

export interface UpdateContainerInput extends Partial<CreateContainerInput> {
  status?: ContainerStatus;
}

export interface CreateContainerItemInput {
  orderItemId?: string;
  materialId?: string;
  description?: string;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  weightKg: number;
  quantity: number;
  volumeCbm?: number;
  color?: string;
}

export interface UpdateContainerItemInput extends Partial<CreateContainerItemInput> {}

/** Dónde quedó una unidad suelta dentro del contenedor, en cm. */
export interface UnitPosition {
  x: number;
  y: number;
  z: number;
  /** `true` si esa unidad va girada 90°. */
  r: boolean;
}

export interface BulkPositionUpdate {
  positions: {
    itemId: string;
    positionX: number;
    positionY: number;
    positionZ: number;
    isRotated: boolean;
    /**
     * El acomodo de cada unidad de la línea.
     *
     * Sin esto, una línea de 200 teclados solo podía guardar UNA posición y el
     * armado se perdía al recargar. `positionX/Y/Z` quedan como la posición de
     * la primera unidad.
     */
    unitPositions?: UnitPosition[];
  }[];
  usedVolumeCbm: number;
  usedWeightKg: number;
  fillPercent: number;
}

export interface CreateContainerTypeInput {
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  maxVolumeCbm: number;
  maxWeightKg: number;
}

// ── Available Container (for availability map) ──────────────────

export interface AvailableContainer {
  id: string;
  containerNumber: string;
  sealNumber: string | null;
  status: ContainerStatus;
  originPort: string | null;
  destinationPort: string | null;
  etd: string | null;
  eta: string | null;
  usedVolumeCbm: number;
  usedWeightKg: number;
  fillPercent: number;
  availableVolumeCbm: number;
  availableWeightKg: number;
  containerType: {
    name: string;
    code: string;
    maxVolumeCbm: number;
    maxWeightKg: number;
  };
  order: {
    id: string;
    orderNumber: string;
  };
  itemCount: number;
}

export interface PortSummary {
  port: string;
  lat: number | null;
  lng: number | null;
  country: string | null;
  totalContainers: number;
  byStatus: {
    empty: number;
    loading: number;
    sealed: number;
  };
  avgFillPercent: number;
  totalAvailableVolumeCbm: number;
  totalAvailableWeightKg: number;
  containers: AvailableContainer[];
}

// ── Auto Calculate ──────────────────────────────────────────────

export interface AutoCalculateItemResult {
  id: string;
  description: string;
  fob: number;
  allocatedFreight: number;
  allocatedInsurance: number;
  cif: number;
  dutyRate: number;
  dutyAmount: number;
  allocatedTax: number;
  allocatedInternacion: number;
  allocatedOtherCosts: number;
  totalItemLandedCost: number;
  unitLandedCost: number;
  quantity: number;
}

export interface AutoCalculateResult {
  costs: {
    fob: number;
    freight: number;
    insurance: number;
    cif: number;
    duties: number;
    taxes: number;
    internacion: number;
    otherCosts: number;
    totalLandedCost: number;
  };
  items: AutoCalculateItemResult[];
  itemCount: number;
  totalWeight: number;
  totalVolume: number;
  containerSuggestion: ContainerSuggestion | null;
  freightBreakdown?: FreightLegBreakdownItem[];
  incotermApplied?: string;
  totalEstimatedDays?: number | null;
}

export interface ContainerSuggestion {
  containerTypeId: string;
  containerTypeName: string;
  containerTypeCode: string;
  quantity: number;
  maxVolumeCbm: number;
  maxWeightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

// ── Import Order Legs ─────────────────────────────────────────────

export type ImportLegStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'DELAYED' | 'CANCELLED';

export type FreightRouteCategory =
  | 'MARITIME_DIRECT'
  | 'AIR_EXPRESS'
  | 'AIR_CONSOLIDATED'
  | 'LAND_REGIONAL'
  | 'MULTIMODAL_LATAM'
  | 'MULTIMODAL_OVERSEAS'
  | 'COURIER'
  | 'OTHER';

export type FreightPricingType = 'WEIGHT' | 'VOLUME' | 'FLAT';

export const FREIGHT_PRICING_LABEL: Record<FreightPricingType, string> = {
  WEIGHT: 'Por peso (USD/kg)',
  VOLUME: 'Por volumen (USD/m³)',
  FLAT: 'Fijo (USD)',
};

export interface FreightService {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  description: string | null;
  category: string | null;
  isActive: boolean;
  sapCode?: string | null;
  // Precio referencial del catálogo (reemplaza el pricing por proveedor).
  referencePrice?: number | string | null;
  priceCurrency?: string | null;
  pricingType?: 'FLAT' | 'WEIGHT' | 'VOLUME' | null;
  _count?: { pricings: number };
  createdAt: string;
  updatedAt: string;
}

export interface FreightServiceProviderPricing {
  id: string;
  tenantId: string;
  serviceId: string;
  providerId: string;
  pricingType: FreightPricingType;
  unitPrice: number | string;
  currency: string;
  minimumCharge: number | string | null;
  validFrom: string | null;
  validUntil: string | null;
  notes: string | null;
  isActive: boolean;
  service?: { id: string; code: string; name: string; category: string | null };
  provider?: { id: string; name: string };
  createdAt: string;
  updatedAt: string;
}

export interface ImportOrderLegService {
  id: string;
  legId: string;
  serviceId: string | null;
  providerId: string | null;
  name: string;
  pricingType: FreightPricingType;
  unitPrice: number | string;
  minimumCharge: number | string | null;
  calculatedAmount: number | string;
  currency: string;
  notes: string | null;
  providerCostAmount: number | null;
  customerSalePrice: number | null;
  service?: { id: string; code: string; name: string; category: string | null } | null;
  provider?: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportOrderLeg {
  id: string;
  orderId: string;
  legOrder: number;
  origin: string;
  destination: string;
  transportMode: TransportMode;
  legType: 'INLAND_ORIGIN' | 'INTERNATIONAL' | 'INLAND_DESTINATION' | 'HANDLING';
  status: ImportLegStatus;
  expectedStart: string | null;
  expectedEnd: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  carrier: string | null;
  trackingRef: string | null;
  notes: string | null;
  plannedAmount: number | null;
  currency: string;
  // Cargo + pricing snapshot (Phase 1+2)
  cargoWeightKg: number | string | null;
  cargoVolumeCbm: number | string | null;
  legCatalogId: string | null;
  providerId: string | null;
  pricingType: FreightPricingType | null;
  unitPrice: number | string | null;
  minimumCharge: number | string | null;
  calculatedAmount: number | string | null;
  // Commercial breakdown fields (Phase 3)
  providerCostAmount: number | null;
  customerSalePrice: number | null;
  provider?: { id: string; name: string } | null;
  legCatalog?: { id: string; origin: string; destination: string } | null;
  services?: ImportOrderLegService[];
  trackingEvents?: TrackingEvent[];
  createdAt: string;
  updatedAt: string;
}

export const LEG_STATUS_LABEL: Record<ImportLegStatus, string> = {
  PENDING: 'Pendiente',
  IN_PROGRESS: 'En curso',
  COMPLETED: 'Completado',
  DELAYED: 'Atrasado',
  CANCELLED: 'Cancelado',
};

export const ROUTE_CATEGORY_LABEL: Record<FreightRouteCategory, string> = {
  MARITIME_DIRECT: 'Marítimo directo',
  AIR_EXPRESS: 'Aéreo express',
  AIR_CONSOLIDATED: 'Aéreo consolidado',
  LAND_REGIONAL: 'Terrestre regional',
  MULTIMODAL_LATAM: 'Multimodal LATAM',
  MULTIMODAL_OVERSEAS: 'Multimodal overseas',
  COURIER: 'Courier',
  OTHER: 'Otro',
};

/**
 * Un producto de la orden que todavía no está estibado en ningún contenedor.
 *
 * Es lo que evita cargar la mercadería dos veces: los productos entran una vez
 * con la proforma y de ahí se estiban.
 */
export interface PendingStowItem {
  orderItemId: string;
  description: string;
  hsCode: string | null;
  totalQuantity: number;
  alreadyStowed: number;
  pendingQuantity: number;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  weightKg: number | null;
  /** `true` si le falta alguna medida: sin las tres no se puede estibar. */
  missingDimensions: boolean;
}
